import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Link,
  Paper,
  Divider,
  styled,
  Avatar,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  Person,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  ArrowForward,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import useAxios from "../useAxios";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
const AuthCard = styled(Paper)(({ theme }) => ({
  maxWidth: 460,
  width: "100%",
  padding: theme.spacing(4),
  borderRadius: 20,
  backgroundColor: "rgba(255,255,255,0.8)",
  backdropFilter: "blur(12px)",
  boxShadow: "0 10px 40px rgba(183,28,28,0.25)",
  [theme.breakpoints.down("sm")]: {
    // padding: theme.spacing(3),
    borderRadius: 16,
    maxWidth: "80%",
    margin: "0 auto",
  },
}));

export default function RegisterForm({ onRegister, onSwitchToLogin }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const axiosData = useAxios();
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [mobileError, setMobilError] = useState("");
  const [emailFormatError, setEmailFormatError] = useState("");
  const [user, setUser] = useState({
    name: "",
    email: "",
    password: "",
    mobile: "",
    city: "",
    confirmPassword: "",
    company_name: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const isFormValid = () => {
    return (
      user.name.trim() !== "" &&
      // user.company_name.trim() !== "" &&
      user.mobile &&
      isValidMobile(user.mobile) &&
      user.email.trim() !== "" &&
      isValidEmail(user.email) &&
      user.city.trim() !== "" &&
      user.password.trim() !== "" &&
      isStrongPassword(user.password) &&
      user.confirmPassword.trim() !== "" &&
      confirmPasswordError === "" &&
      passwordError === "" &&
      emailFormatError === "" &&
      user.password === user.confirmPassword &&
      !loading
    );
  };

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };
  const isValidMobile = (mobile) => /^[0-9]{10}$/.test(mobile); // Assumes 10-digit mobile number

  const isStrongPassword = (password) => password.length >= 6; // Minimum 6 characters

  const handleChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
    const { name, value } = e.target;
    if (user.email && !isValidEmail(user.email)) {
      setEmailFormatError("Please enter a valid email address");
      return;
    }else{
      setEmailFormatError("");
     setEmailError("");
    }
    if (user.password && !isStrongPassword(user.password)) {
      setPasswordError("Please enter minimum 6 characters");
      return;
    }else{
    setPasswordError("");
    }
    // Check confirm password mismatch
    if (name === "confirmPassword" || name === "password") {
      if (
        (name === "confirmPassword" && value !== user.password) ||
        (name === "password" &&
          user.confirmPassword &&
          user.confirmPassword !== value)
      ) {
        setConfirmPasswordError("Passwords do not match");
      } else if (
        (name === "password" && value !== user.password) ||
        (name === "password" && user.password && user.password !== value)
      ) {
        setPasswordError("");
      } else {
        setPasswordError("");
        setConfirmPasswordError("");
      }
    }
  };

  const handleSubmit = async (e) => {
    setEmailError("");
    setMobilError("");
    setEmailFormatError("");
    setLoading(true);
    e.preventDefault();
    // Basic validation (example)
    if (user.password !== user.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    const cityValue =
      user.city.charAt(0).toUpperCase() + user.city.slice(1).toLowerCase();
  
    try {
      const response = await axiosData.post("/register", {
        name: user.name,
        email: user.email,
        password: user.password,
        mobile_number: parseInt(user.mobile),
        city: cityValue,
        company_name: user.company_name,
        user_type: "user",
        status: "Active",
      });
      // Call parent callback with success response
      setSuccessDialogOpen(true);
      // onRegister(response.data);
      // Optional: Reset form
      setUser({
        name: "",
        email: "",
        password: "",
        mobile: "",
        city: "",
        confirmPassword: "",
        company_name: "",
      });
    } catch (error) {
      console.log(error);
      const msg = error.response?.data?.message || "Something went wrong";
      console.error("Registration failed:", msg);
      if (msg.toLowerCase().includes("email")) {
        setEmailError(msg); // show error below email field
      } else if (msg.toLowerCase().includes("mobile")) {
        setMobilError(msg);
      } else {
        alert(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ x: 50, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: theme.spacing(3),
      }}
    >
      <AuthCard>
        <Box textAlign="center" mb={isMobile ? 2 : 3}>
          <Avatar
            src="/Logo2.png"
            variant="square"
            sx={{
              width: isMobile ? "80%" : "60%",
              mx: "auto",
              height: "auto",
              maxWidth: 300,
            }}
          />
          <Typography
            color="text.secondary"
            variant={isMobile ? "body2" : "body1"}
            sx={{ mt: 1 }}
          >
            Create your account
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <FormControl fullWidth margin="normal">
            <TextField
              name="name"
              label="Full Name"
              variant="outlined"
              required
              placeholder="Enter your full name"
              value={user.name}
              onChange={handleChange}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person
                      color="action"
                      fontSize={isMobile ? "small" : "medium"}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 12,
                },
              }}
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <TextField
              name="company_name"
              label="Company Name"
              variant="outlined"
              placeholder="Enter your company name"
              value={user.company_name}
              onChange={handleChange}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person
                      color="action"
                      fontSize={isMobile ? "small" : "medium"}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 12,
                },
              }}
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <TextField
              name="mobile"
              label="Mobile Number"
              variant="outlined"
              required
              type="tel"
              placeholder="Enter your mobile number"
              value={user.mobile}
             onChange={(e) => {
  const value = e.target.value.replace(/[^0-9]/g, ""); // ? allow only digits
  setUser({ ...user, [e.target.name]: value });
}}
              error={
                (user.mobile && !isValidMobile(user.mobile)) || !!mobileError
              }
              helperText={
                user.mobile && !isValidMobile(user.mobile)
                  ? "Enter a valid 10-digit number"
                  : mobileError || ""
              }
              size={isMobile ? "small" : "medium"}
              inputProps={{
                pattern: "^[0-9]{8,15}$", // only digits, 8�15
                maxLength: 15, // max 15 digits
                inputMode: "numeric", // mobile keyboard shows numbers only
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneAndroidIcon
                      color="action"
                      fontSize={isMobile ? "small" : "medium"}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 12,
                },
                 "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: "#000 !important",
                  transition: "background-color 5000s ease-in-out 0s",
                  caretColor: "#000",
                },
              }}
            />
          </FormControl>{" "}
          {/* <Typography color="error" sx={{ fontSize: 13, marginLeft: 3 }}>
            Note: Please enter a valid WhatsApp number to receive the OTP.
          </Typography> */}
          <FormControl fullWidth margin="normal">
            <TextField
              name="email"
              label="Email"
              type="email"
              variant="outlined"
              required
              placeholder="Enter your email"
              value={user.email}
              onChange={handleChange}
              onInput={handleChange} 
              onBlur={handleChange}
              size={isMobile ? "small" : "medium"}
              error={!!emailError || !!emailFormatError}
              helperText={emailFormatError || emailError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email
                      color="action"
                      fontSize={isMobile ? "small" : "medium"}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 12,
                },
                 "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: "#000 !important",
                  transition: "background-color 5000s ease-in-out 0s",
                  caretColor: "#000",
                },
              }}
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <TextField
              name="city"
              label="City"
              variant="outlined"
              required
              placeholder="Enter your city"
              value={user.city}
              onChange={handleChange}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon
                      color="action"
                      fontSize={isMobile ? "small" : "medium"}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 12,
                },
              }}
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <TextField
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              required
              placeholder="Create a password"
              value={user.password}
              onChange={handleChange}
              error={!!passwordError}
              helperText={passwordError}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock
                      color="action"
                      fontSize={isMobile ? "small" : "medium"}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size={isMobile ? "small" : "medium"}
                    >
                      {showPassword ? (
                        <VisibilityOff
                          fontSize={isMobile ? "small" : "medium"}
                        />
                      ) : (
                        <Visibility fontSize={isMobile ? "small" : "medium"} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 12,
                },
                 "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px transparent inset !important",
                  WebkitTextFillColor: "#000 !important",
                  transition: "background-color 5000s ease-in-out 0s",
                  caretColor: "#000",
                },
              }}
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <TextField
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              required
              placeholder="Confirm password"
              value={user.confirmPassword}
              onChange={handleChange}
              size={isMobile ? "small" : "medium"}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock
                      color="action"
                      fontSize={isMobile ? "small" : "medium"}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                      size={isMobile ? "small" : "medium"}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff
                          fontSize={isMobile ? "small" : "medium"}
                        />
                      ) : (
                        <Visibility fontSize={isMobile ? "small" : "medium"} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 12,
                },
              }}
            />
          </FormControl>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={!isFormValid()}
            //disabled={loading}
            size={isMobile ? "medium" : "large"}
            type="submit"
            endIcon={<ArrowForward fontSize={isMobile ? "small" : "medium"} />}
            sx={{
              mt: 3,
              py: isMobile ? 1 : 1.5,
              borderRadius: 12,
              fontSize: isMobile ? "0.875rem" : "1rem",
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: "#D32F2F",
              "&:hover": {
                backgroundColor: "#B71C1C",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(211, 47, 47, 0.5)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Create Account"
            )}
          </Button>
          <Divider sx={{ my: isMobile ? 2 : 3 }}>
            <Typography
              variant={isMobile ? "caption" : "body2"}
              color="text.secondary"
            >
              OR
            </Typography>
          </Divider>
          <Box textAlign="center" mt={isMobile ? 1 : 2}>
            <Typography variant={isMobile ? "caption" : "body2"}>
              Already have an account?{" "}
              <Link
                component="button"
                variant={isMobile ? "caption" : "body2"}
                underline="always"
                color="primary"
                fontWeight={500}
                onClick={onSwitchToLogin}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        </form>
        <Dialog
          open={successDialogOpen}
          onClose={() => setSuccessDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              padding: "24px",
              textAlign: "center",
              maxWidth: "450px",
            },
          }}
        >
          <Box textAlign="center" mb={2}>
            <CheckCircleOutlineIcon
              color="success"
              sx={{ fontSize: 80, color: "#4caf50" }}
            />
          </Box>

          <DialogTitle
            sx={{
              fontSize: "1.8rem",
              fontWeight: 700,
              color: "#2e7d32",
              padding: 0,
              mb: 2,
            }}
          >
            Registration Successful!
          </DialogTitle>

          <DialogContent sx={{ padding: 0, mb: 3 }}>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: "1.1rem",
              }}
            >
              Your account has been created successfully. You can now sign in
              and start exploring.
            </Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", padding: 0 }}>
            <Button
              onClick={() => {
                setSuccessDialogOpen(false);
                setConfirmPasswordError("");
                onSwitchToLogin();
              }}
              color="success"
              variant="contained"
              size="large"
              sx={{
                borderRadius: "12px",
                padding: "10px 24px",
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: "#388e3c",
                },
              }}
              autoFocus
            >
              Continue to Sign In
            </Button>
          </DialogActions>
        </Dialog>
      </AuthCard>
    </motion.div>
  );
}
